import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from '../../config/api';
import { logoutUser } from './authSlice';

const mapClientToCustomer = (client, fallback = {}) => {
  const isRemoved = Boolean(client?.isRemoved ?? client?.cmFlagRemove ?? false);
  let isBlocked = Boolean(client?.isBlocked ?? client?.cmFlagBlock ?? false);
  if (client?.status != null) {
    isBlocked = isBlocked || client.status !== 'active';
  }

  let accountStatus = '有効';
  if (isRemoved) {
    accountStatus = '削除済み';
  } else if (isBlocked) {
    accountStatus = '停止中';
  }

  return {
    id: client?.id,
    clientId:
      client?.loginId ??
      client?.clientId ??
      (client?.id != null ? String(client.id) : ''),
    email: client?.email ?? fallback.email ?? '',
    storeName:
      client?.nickName ??
      client?.nickname ??
      fallback.storeName ??
      '',
    accountStatus,
    tel: client?.tel,
    dateReg: client?.dateReg,
    status: client?.status,
    isRemoved,
    isBlocked,
  };
};

export const fetchCustomers = createAsyncThunk(
  'customer/fetchCustomers',
  async (searchQuery, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const response = await fetch(API_ENDPOINTS.ADMIN_CLIENTS, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return rejectWithValue(result?.message || 'クライアント一覧の取得に失敗しました');
      }

      const rows = Array.isArray(result?.data) ? result.data : [];
      const mapped = rows.map((client) => mapClientToCustomer(client));

      const q = (searchQuery || '').trim().toLowerCase();
      if (!q) return mapped;
      return mapped.filter((c) =>
        String(c.storeName || '').toLowerCase() === q
      );
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customer/createCustomer',
  async (customerData, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const response = await fetch(API_ENDPOINTS.ADMIN_CLIENTS, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: customerData.email,
          nickname: customerData.storeName,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'クライアントの作成に失敗しました');
      }
      const client = data?.client || data?.data || data;
      return mapClientToCustomer(client, {
        email: customerData.email,
        storeName: customerData.storeName,
        
      });
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const toggleCustomerStatus = createAsyncThunk(
  'customer/toggleCustomerStatus',
  async ({ customerId, currentStatus }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const isCurrentlyBlocked = currentStatus === '停止中';
      const endpoint = isCurrentlyBlocked
        ? API_ENDPOINTS.ADMIN_CLIENT_UNBLOCK(customerId)
        : API_ENDPOINTS.ADMIN_CLIENT_BLOCK(customerId);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return rejectWithValue(data?.message || '状態の変更に失敗しました');
      }

      const newStatus = isCurrentlyBlocked ? '有効' : '停止中';
      return { customerId, newStatus };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customer/deleteCustomer',
  async (customerId, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const response = await fetch(API_ENDPOINTS.ADMIN_CLIENT(customerId), {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return rejectWithValue(data?.message || '削除に失敗しました');
      }

      return customerId;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

const initialState = {
  customers: [],
  searchQuery: '',
  currentPage: 1,
  itemsPerPage: 9,
  isLoading: false,
  error: null,
};

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    upsertCustomerFromSocket: (state, action) => {
      const mapped = mapClientToCustomer(action.payload || {});
      if (mapped?.id == null) return;
      const idx = state.customers.findIndex((c) => c.id === mapped.id);
      if (idx === -1) {
        state.customers.unshift(mapped);
        state.currentPage = 1;
      } else {
        state.customers[idx] = { ...state.customers[idx], ...mapped };
      }
    },
    updateCustomerStatusFromSocket: (state, action) => {
      const { id, accountStatus } = action.payload || {};
      if (id == null || !accountStatus) return;
      state.customers = state.customers.map((c) =>
        c.id === id ? { ...c, accountStatus } : c
      );
    },
    markCustomerRemovedFromSocket: (state, action) => {
      const id = action.payload?.id;
      if (id == null) return;
      state.customers = state.customers.map((c) =>
        c.id === id
          ? { ...c, isRemoved: true, accountStatus: '削除済み' }
          : c
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload;
        state.currentPage = 1;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createCustomer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers.unshift(action.payload);
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleCustomerStatus.fulfilled, (state, action) => {
        const { customerId, newStatus } = action.payload;
        state.customers = state.customers.map(customer =>
          customer.id === customerId
            ? { ...customer, accountStatus: newStatus }
            : customer
        );
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        const customerId = action.payload;
        state.customers = state.customers.map((customer) =>
          customer.id === customerId
            ? {
              ...customer,
              isRemoved: true,
              accountStatus: '削除済み',
            }
            : customer
        );
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export const {
  setSearchQuery,
  setCurrentPage,
  clearError,
  upsertCustomerFromSocket,
  updateCustomerStatusFromSocket,
  markCustomerRemovedFromSocket,
} = customerSlice.actions;

export default customerSlice.reducer;
